// src/pages/Documents.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import axiosInstance from "../axiosInstance";
import { useNavigate } from "react-router-dom";

/* Popups */
import FactureModal from "../components/FactureModal";
import ModalAcompte from "../components/ModalAcompte";
import ModalPartielle from "../components/ModalPartielle";
/* ================= Styles boutons (globaux) ================= */
const BTN_BASE =
  "inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition focus:outline-none focus:ring-2 focus:ring-sky-300";
const btnGray = `${BTN_BASE} bg-gray-200 hover:bg-gray-300 text-slate-700`;
const btnBlue = `${BTN_BASE} bg-sky-700 hover:bg-sky-600 text-white`;
export const btnSolid = btnBlue;
export const btnGhost = `${BTN_BASE} bg-gray-100 hover:bg-gray-200 text-slate-800 border border-gray-200`;

/* ================= Utils ================= */
const money = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const toMonthKey = (iso) => {
  if (!iso) return "0000-00";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "0000-00";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const toMonthLabel = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(d);
};

const fmtNum = (kind, raw, id) => {
  const prefix = kind === "facture" ? "FAC-" : "DEV-";
  const s = String(raw ?? "").trim();
  if (s.startsWith(prefix)) return s;
  if (/^[A-Z]{3,}-/.test(s)) return s;
  if (!s && id != null) return `${prefix}${String(id).padStart(4, "0")}`;
  return `${prefix}${s}`;
};
// Empêche la ligne cliquable d’ouvrir la page quand on interagit avec un menu/btn interne
const stopRowNav = (e) => {
  e.preventDefault?.();
  e.stopPropagation?.();
};

const normalizeEtatKey = (v = "") => String(v).toLowerCase().replace(/_/g, " ");
const isFacturePayee = (f = {}) => ["payée", "payee"].includes(normalizeEtatKey(f.etat ?? f.status ?? ""));
const sumFacturesPayees = (factures = []) =>
  factures.filter(isFacturePayee).reduce((s, f) => s + Number(f.montant_ttc ?? f.total_ttc ?? 0), 0);

const BADGE_CLASSES = {
  "en cours": "bg-gray-200 text-gray-800",
  en_cours: "bg-gray-200 text-gray-800",
  brouillon: "bg-gray-200 text-gray-800",

  // devis
  "envoyé": "bg-amber-100 text-amber-700",
  envoye: "bg-amber-100 text-amber-700",
  "accepté": "bg-sky-100 text-sky-700",
  accepte: "bg-sky-100 text-sky-700",
  acceptee: "bg-sky-100 text-sky-700",
  "abandonné": "bg-red-100 text-red-700",
  abandonne: "bg-red-100 text-red-700",
  abandonner: "bg-red-100 text-red-700",

  // factures
  "envoyée": "bg-amber-100 text-amber-700",
  envoyee: "bg-amber-100 text-amber-700",
  "payée": "bg-emerald-100 text-emerald-700",
  payee: "bg-emerald-100 text-emerald-700",
  "abandonnée": "bg-red-100 text-red-700",
  abandonnee: "bg-red-100 text-red-700",
  "finalisée": "bg-orange-100 text-orange-700",
  finalisee: "bg-orange-100 text-orange-700",
};

/* ==== Grilles d'alignement (largeurs stables + chiffres tabulaires) ==== */
const INFO_GRID =
  "grid w-full items-center gap-x-4 gap-y-1 " +
  "grid-cols-[110px_1fr] " +
  "sm:grid-cols-[110px_minmax(180px,210px)_minmax(220px,1fr)_minmax(260px,1.4fr)]";
const METRICS_GRID = "grid grid-cols-3 gap-4 justify-items-end min-w-[260px] text-[13px] tabular-nums";
const METRIC_CELL = "inline-flex items-center gap-1 whitespace-nowrap";
const METRIC_VALUE = "font-semibold";

/* ================= Libellés ↔ clés (DEVIS) ================= */
const DEVIS_LABELS = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  accepte: "Accepté",
  abandonne: "Abandonné",
};
const labelToKeyDevis = (lib) => {
  const s = String(lib || "").toLowerCase();
  if (["brouillon", "en cours", "en_cours", ""].includes(s)) return "brouillon";
  if (s.includes("envoy")) return "envoye";
  if (s.includes("accept")) return "accepte";
  if (s.includes("abandon")) return "abandonne";
  return "brouillon";
};
const keyToLabelDevis = (keyOrLib) => {
  const s = String(keyOrLib || "").toLowerCase().replace(/_/g, " ");
  if (s.includes("envoy")) return DEVIS_LABELS.envoye;
  if (s.includes("accept")) return DEVIS_LABELS.accepte;
  if (s.includes("abandon")) return DEVIS_LABELS.abandonne;
  return DEVIS_LABELS.brouillon;
};
/* Helpers robustes : lis status OU etat (retours API hétérogènes) */
const getDevisKey = (d = {}) => {
  const raw = String(d.status ?? d.etat ?? "").toLowerCase();
  if (raw.includes("accept")) return "accepte";
  if (raw.includes("abandon")) return "abandonne";
  if (raw.includes("envoy")) return "envoye";
  if (raw.includes("brouillon") || raw.includes("en cours") || raw.includes("en_cours")) return "brouillon";
  return "brouillon";
};
const getDevisLabel = (d = {}) => keyToLabelDevis(getDevisKey(d));
const isDevisAccepte = (d = {}) => getDevisKey(d) === "accepte";

/* ================= API states ================= */
/* Devis : essaye plusieurs routes/corps pour absorber 404/400/409 */
async function persistEtatDevis(axios, id, etatLibelleOuCle) {
  const status = labelToKeyDevis(etatLibelleOuCle);

  // candidates: [ [method, url, body], ... ]
  const attempts = [
    ["patch", `/devis/${id}`, { status }],        // Rails/Laravel style (colonne status)
    ["patch", `/devis/${id}`, { etat: status }],  // certains backends lisent encore "etat"
    ["patch", `/devis/${id}/etat`, { etat: status }], // ancien endpoint
    ["put", `/devis/${id}`, { status }],
    ["put", `/devis/${id}`, { etat: status }],
  ];

  let lastErr = null;
  for (const [method, url, body] of attempts) {
    try {
      const res = await axios[method](url, body);
      return res?.data ?? true;
    } catch (e) {
      const code = e?.response?.status;
      // 409 = déjà dans cet état → OK silencieux
      if (code === 409) return true;
      // 404/400/405 → on tente la suivante
      lastErr = e;
      continue;
    }
  }
  console.warn("Maj état devis échouée:", lastErr?.response?.status, lastErr?.message);
  throw lastErr || new Error("persistEtatDevis failed");
}

async function persistEtatFacture(axios, id, etat) {
  const v = normalizeEtatKey(etat);
  let apiEtat = "envoyee";
  if (v.includes("pay")) apiEtat = "payee";
  if (v.includes("abandon")) apiEtat = "abandonnee";
  try {
    return (await axios.patch(`/factures/${id}/etat`, { etat: apiEtat })).data;
  } catch {
    try {
      return (await axios.put(`/factures/${id}`, { etat: apiEtat })).data;
    } catch (e2) {
      console.warn("Maj état facture échouée:", e2?.response?.status, e2?.message);
      return null;
    }
  }
}

/* ================= Relance (localStorage) ================= */
const RELANCE_STORAGE_KEY = "docs:relances";
const loadRelanceMap = () => {
  try {
    return JSON.parse(localStorage.getItem(RELANCE_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};
const saveRelanceMap = (map) => {
  localStorage.setItem(RELANCE_STORAGE_KEY, JSON.stringify(map));
  localStorage.setItem("docs:refresh", String(Date.now()));
};
const isRelanceMarked = (id) => !!loadRelanceMap()[id];
const markRelance = (id, marked) => {
  const m = loadRelanceMap();
  if (marked) m[id] = Date.now();
  else delete m[id];
  saveRelanceMap(m);
};

/* ================= EtatControl générique ================= */
const MENU_BTN_BASE =
  "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1";

function EtatControl({
  value,
  options,
  onChange,
  onPersist,
  badgeFallback = "bg-gray-100 text-gray-700",
  compact = false,
  menuAlign = "right",
  ariaLabel = "Changer l'état",
  rollbackOnError = true,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const label = value || options[0];
  const cls = BADGE_CLASSES[normalizeEtatKey(label)] || badgeFallback;
  const close = () => setOpen(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target))
        close();
    };
    const onKey = (e) => open && e.key === "Escape" && close();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const setEtat = async (etat) => {
    close();
    const prev = value || options[0];
    onChange(etat);
    try {
      await onPersist?.(etat);
    } catch (e) {
      console.warn("Maj état échouée:", e?.response?.status, e?.message);
      if (rollbackOnError) onChange(prev);
    }
  };

  const menuPos = menuAlign === "left" ? "left-0" : "right-0";

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`${MENU_BTN_BASE} ${cls} ${compact ? "px-2 py-0" : ""}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (["ArrowDown", "Enter", " "].includes(e.key)) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label}
        <span className="ml-1 text-gray-500">▾</span>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={`absolute ${menuPos} mt-1 z-50 w-44 bg-white border rounded shadow text-xs py-1`}
        >
          {options.map((opt) => (
            <button
              key={opt}
              role="menuitem"
              className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              onClick={() => setEtat(opt)}
            >
              {opt}
            </button>
          ))}
          <div className="my-1 border-t" />
        </div>
      )}
    </div>
  );
}

/* ================= Menus d'état spécifiques ================= */
function FactureEtatControl({ facture, onChange, enableRelance = false }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const value = facture.etat || "Envoyée";
  const paid = isFacturePayee(facture);
  const abandoned = normalizeEtatKey(value).includes("abandon");
  const [relanceMarked, setRelanceMarked] = useState(isRelanceMarked(facture.id));
  const baseCls = BADGE_CLASSES[normalizeEtatKey(value)] || BADGE_CLASSES["envoyée"];

  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => open && e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const setEtat = async (etatLibelle) => {
    setOpen(false);
    const prev = value;
    onChange?.(etatLibelle);
    try {
      await persistEtatFacture(axiosInstance, facture.id, etatLibelle);
      const nk = normalizeEtatKey(etatLibelle);
      if (nk.includes("pay") || nk.includes("abandon")) {
        markRelance(facture.id, false);
        setRelanceMarked(false);
      }
      localStorage.setItem("docs:refresh", String(Date.now()));
    } catch (e) {
      onChange?.(prev);
      alert("❌ Impossible de mettre à jour l'état de la facture.");
      console.error(e);
    }
  };

  const toggleRelanceLocal = () => {
    setOpen(false);
    if (paid || abandoned) return;
    const next = !relanceMarked;
    markRelance(facture.id, next);
    setRelanceMarked(next);
  };

  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        ref={btnRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Actions facture"
        className={`${MENU_BTN_BASE} ${baseCls} px-2 py-0`}
        onClick={() => setOpen((v) => !v)}
      >
        {value}
        <span className="ml-1 text-gray-500">▾</span>
      </button>

      {enableRelance && relanceMarked && !paid && !abandoned && (
        <span
          className="ml-1 px-2 py-0.5 rounded text-[10px] uppercase bg-sky-700 text-white"
          title="Relancée (repère visuel)"
        >
          Relancée
        </span>
      )}

      {open && (
        <div ref={menuRef} role="menu" className="absolute right-0 mt-1 z-50 w-56 bg-white border rounded shadow text-xs py-1">
          <button role="menuitem" className="w-full text-left px-3 py-2 hover:bg-gray-50" onClick={() => setEtat("Envoyée")}>
            Envoyée
          </button>
          <button role="menuitem" className="w-full text-left px-3 py-2 hover:bg-gray-50" onClick={() => setEtat("Payée")}>
            Payée
          </button>
          <button
            role="menuitem"
            className="w-full text-left px-3 py-2 hover:bg-gray-50"
            onClick={() => setEtat("Abandonnée")}
            title="Ne sera pas comptabilisée"
          >
            Abandonnée
          </button>
          {enableRelance && (
            <>
              <div className="my-1 border-t" />
              <button
                role="menuitem"
                onClick={toggleRelanceLocal}
                disabled={paid || abandoned}
                className={
                  "w-full text-left px-3 py-2 hover:bg-gray-50 " +
                  (paid || abandoned ? "opacity-50 cursor-not-allowed" : "")
                }
              >
                {relanceMarked ? "Retirer l’indicateur de relance" : "Marquer relancée (repère visuel)"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DevisEtatControl({ devis, onChange }) {
  // ordre voulu : Brouillon → Envoyé → Accepté → Abandonné
  const options = ["Brouillon", "Envoyé", "Accepté", "Abandonné"];

  return (
    <EtatControl
      value={getDevisLabel(devis)}
      options={options}
      badgeFallback={BADGE_CLASSES["brouillon"]}
      ariaLabel="Changer l'état du devis"
      onChange={(etat) => onChange(etat)}
      onPersist={async (etat) => {
        const res = await persistEtatDevis(axiosInstance, devis.id, etat);
        if (res) localStorage.setItem("docs:refresh", String(Date.now()));
        else throw new Error("persistEtatDevis failed");
      }}
      menuAlign="right"
      rollbackOnError={true}
    />
  );
}

/* ================= Export utils ================= */
const dl = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 0);
};

const escapeCsv = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[";,\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const toCsv = (rows, headers) => {
  const head = headers.map((h) => escapeCsv(h.label)).join(";");
  const body = rows.map((r) => headers.map((h) => escapeCsv(r[h.key])).join(";")).join("\n");
  return head + "\n" + body;
};

const mapDevis = (d) => ({
  id: d.id,
  numero: fmtNum("devis", d.numero_devis, d.id),
  date: d.date_devis || d.created_at || "",
  client_nom: d.client_nom || "",
  client_adresse: d.client_adresse || "",
  projet: d.projet_nom || d.projet || "",
  etat: getDevisLabel(d),
  total_ht: Number(d.total_ht ?? 0),
  total_ttc: Number(d.total_ttc ?? 0),
});
const mapFacture = (f) => ({
  id: f.id,
  numero: fmtNum("facture", f.numero || f.numero_facture, f.id),
  type: String(f.type_facture ?? f.type ?? "facture").toLowerCase(),
  date: f.date || f.date_facture || f.created_at || "",
  paid_at: f.paid_at || "",
  client_nom: f.client_nom || f.client?.nom || "",
  client_adresse: f.client_adresse || f.client?.adresse || "",
  etat: f.etat || f.status || "",
  total_ht: Number(f.total_ht ?? f.montant_ht ?? 0),
  total_ttc: Number(f.total_ttc ?? f.montant_ttc ?? f.total ?? 0),
});

/* ================= Export Modal ================= */
function ExportModal({ open, onClose, devis, factures, defaults }) {
  const [what, setWhat] = useState(defaults.what || "factures");
  const [fmt, setFmt] = useState(defaults.fmt || "csv");
  const [from, setFrom] = useState(defaults.from || "");
  const [to, setTo] = useState(defaults.to || "");
  const [client, setClient] = useState(defaults.client || "");
  const [onlyPaid, setOnlyPaid] = useState(false);

  useEffect(() => {
    setWhat(defaults.what || "factures");
    setFmt(defaults.fmt || "csv");
    setFrom(defaults.from || "");
    setTo(defaults.to || "");
    setClient(defaults.client || "");
  }, [open, defaults]);

  if (!open) return null;

  const inRange = (d) => {
    const t = (v) => (v ? new Date(v).getTime() : NaN);
    const ts = t(d);
    if (Number.isNaN(ts)) return true;
    if (from && ts < new Date(from).getTime()) return false;
    if (to && ts > new Date(to).getTime() + 24 * 3600 * 1000 - 1) return false;
    return true;
  };

  const applyFilters = () => {
    let outDevis = [];
    let outFactures = [];
    if (what === "devis" || what === "both") {
      outDevis = devis.map(mapDevis).filter((r) => (!client || r.client_nom === client) && inRange(r.date));
    }
    if (what === "factures" || what === "both") {
      outFactures = factures
        .map(mapFacture)
        .filter((r) => (!client || r.client_nom === client) && inRange(r.paid_at || r.date))
        .filter((r) => (!onlyPaid || isFacturePayee({ etat: r.etat })));
    }
    return { outDevis, outFactures };
  };

  const doExport = () => {
    const { outDevis, outFactures } = applyFilters();
    if (fmt === "json") {
      const payload = { exported_at: new Date().toISOString(), devis: outDevis, factures: outFactures };
      dl(`export-${what}-${Date.now()}.json`, "application/json;charset=utf-8", JSON.stringify(payload, null, 2));
      onClose();
      return;
    }
    const devisCols = [
      { key: "id", label: "ID" },
      { key: "numero", label: "Numero" },
      { key: "date", label: "Date" },
      { key: "client_nom", label: "Client" },
      { key: "client_adresse", label: "Adresse" },
      { key: "projet", label: "Projet" },
      { key: "etat", label: "Etat" },
      { key: "total_ht", label: "Total HT" },
      { key: "total_ttc", label: "Total TTC" },
    ];
    const factCols = [
      { key: "id", label: "ID" },
      { key: "numero", label: "Numero" },
      { key: "type", label: "Type" },
      { key: "date", label: "Date" },
      { key: "paid_at", label: "Payee le" },
      { key: "client_nom", label: "Client" },
      { key: "client_adresse", label: "Adresse" },
      { key: "etat", label: "Etat" },
      { key: "total_ht", label: "Total HT" },
      { key: "total_ttc", label: "Total TTC" },
    ];

    if (what === "devis") dl(`devis-${Date.now()}.csv`, "text/csv;charset=utf-8", toCsv(outDevis, devisCols));
    else if (what === "factures") dl(`factures-${Date.now()}.csv`, "text/csv;charset=utf-8", toCsv(outFactures, factCols));
    else {
      dl(`devis-${Date.now()}.csv`, "text/csv;charset=utf-8", toCsv(outDevis, devisCols));
      dl(`factures-${Date.now()}.csv`, "text/csv;charset=utf-8", toCsv(outFactures, factCols));
    }
    onClose();
  };

  const clientsSet = new Set([
    ...devis.map((d) => d.client_nom).filter(Boolean),
    ...factures.map((f) => f.client_nom || f.client?.nom).filter(Boolean),
  ]);
  const clientOptions = Array.from(clientsSet).sort((a, b) =>
    String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
  );

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-[800px] max-w-[95vw]">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Exporter — Devis & Factures</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-1">Documents</div>
              <div className="flex gap-2">
                <label className="inline-flex items-center gap-1">
                  <input type="radio" checked={what === "factures"} onChange={() => setWhat("factures")} /> Factures
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="radio" checked={what === "devis"} onChange={() => setWhat("devis")} /> Devis
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="radio" checked={what === "both"} onChange={() => setWhat("both")} /> Les deux
                </label>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Format</div>
              <div className="flex gap-2">
                <label className="inline-flex items-center gap-1">
                  <input type="radio" checked={fmt === "csv"} onChange={() => setFmt("csv")} /> CSV (Excel)
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="radio" checked={fmt === "json"} onChange={() => setFmt("json")} /> JSON
                </label>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Période</div>
              <div className="flex items-center gap-1">
                <input type="date" className="border rounded px-1 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
                <span>→</span>
                <input type="date" className="border rounded px-1 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Client</div>
              <select className="w-full border rounded px-2 py-1" value={client} onChange={(e) => setClient(e.target.value)}>
                <option value="">Tous</option>
                {clientOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {what !== "devis" && (
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={onlyPaid} onChange={(e) => setOnlyPaid(e.target.checked)} />
                  Exporter uniquement les factures payées
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
          >
            Annuler
          </button>
          <button
            onClick={doExport}
            className="inline-flex items-center rounded-full bg-sky-700 px-4 py-2 text-sm text-white shadow hover:bg-sky-600 transition"
          >
            Exporter
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= Filtres + Recherche (identique Clients) ================= */
function FiltersDropdownDocs({ open, onClose, values, setValues, clientOptions }) {
  const popRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (popRef.current && !popRef.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => open && e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={popRef} className="absolute z-[1200] mt-2 w-[340px] rounded-xl border bg-white shadow-xl p-3">
      <div className="space-y-3">
        {/* Type de document */}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Type</div>
          <select
            className="w-full border rounded px-2 py-1 text-sm"
            value={values.kind || "both"}
            onChange={(e) => setValues((v) => ({ ...v, kind: e.target.value }))}
          >
            <option value="both">(Devis & Factures)</option>
            <option value="devis">Devis</option>
            <option value="factures">Factures</option>
          </select>
        </div>

        {/* Client (datalist) */}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Client</div>
          <input
            list="clients-docs"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Nom du client…"
            value={values.client || ""}
            onChange={(e) => setValues((v) => ({ ...v, client: e.target.value }))}
          />
          <datalist id="clients-docs">
            {clientOptions.slice(0, 100).map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>

        {/* Période */}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Période</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={values.from || ""}
              onChange={(e) => setValues((v) => ({ ...v, from: e.target.value }))}
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={values.to || ""}
              onChange={(e) => setValues((v) => ({ ...v, to: e.target.value }))}
            />
          </div>
        </div>

        {/* Montant TTC min / max */}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Montant TTC (min / max)</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="min"
              value={values.min ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, min: e.target.value }))}
            />
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="max"
              value={values.max ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, max: e.target.value }))}
            />
          </div>
        </div>

        <div className="pt-2 flex items-center justify-end gap-2">
          <button
            className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition bg-white border border-gray-300 text-slate-800 hover:bg-gray-50"
            onClick={() => setValues({ kind: "both", client: "", from: "", to: "", min: "", max: "" })}
          >
            Réinitialiser
          </button>
          <button
            className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition bg-sky-700 hover:bg-sky-600 text-white"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function TopBar({ q, setQ, filters, setFilters, clientOptions, navigate, onExport }) {
  const [open, setOpen] = useState(false);
  const leftRef = useRef(null);
  const btnRef = useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      const inside =
        (leftRef.current && leftRef.current.contains(e.target)) ||
        (btnRef.current && btnRef.current.contains(e.target));
      if (!inside) setOpen(false);
    };
    const onKey = (e) => open && e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="rounded-lg border bg-white px-3 py-2">
      {/* Sur mobile : 1 colonne / Sur md+ : 3 colonnes */}
      <div className="grid gap-3 items-center md:grid-cols-3">
        {/* GAUCHE : Filtres + Recherche */}
        <div className="relative w-full" ref={leftRef}>
          <div className="inline-flex w-full max-w-full items-stretch h-9">
            <button
              ref={btnRef}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="dropdown-filtres-docs"
              className={
                "px-4 text-sm border border-slate-300 rounded-l-full shrink-0 " +
                (open
                  ? "bg-gray-800 text-white"
                  : "bg-white hover:bg-gray-50 text-slate-700")
              }
            >
              Filtres
            </button>
            <input
              className="px-3 text-sm rounded-r-full border border-l-0 border-slate-300 bg-gray-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent flex-1 min-w-0"
              placeholder="Rechercher un devis ou une facture…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Rechercher un document"
              style={{ marginLeft: "-1px" }}
            />
          </div>

          <div id="dropdown-filtres-docs" className="absolute left-0 top-full">
            <FiltersDropdownDocs
              open={open}
              onClose={() => setOpen(false)}
              values={filters}
              setValues={setFilters}
              clientOptions={clientOptions}
            />
          </div>
        </div>

        {/* CENTRE : boutons création */}
        <div className="flex flex-wrap justify-start md:justify-center gap-2">
          <button
            className={btnGhost}
            onClick={() => navigate("/creer-devis")}
          >
            Nouveau devis
          </button>
          <button
            className={btnGhost}
            onClick={() => navigate("/creer-facture")}
          >
            Nouvelle facture
          </button>
        </div>

        {/* DROITE : Export */}
        <div className="justify-self-end">
          <button
            className={btnSolid}
            onClick={onExport}
            aria-label="Exporter les documents"
          >
            Exporter…
          </button>
        </div>
      </div>
    </div>
  );
}


/* ================= Page ================= */
export default function Documents() {
  const [devisList, setDevisList] = useState([]);
  const [facturesLibres, setFacturesLibres] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFactureModal, setShowFactureModal] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [showPartielleModal, setShowPartielleModal] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);

  const [q, setQ] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [filters, setFilters] = useState({ kind: "both", client: "", from: "", to: "", min: "", max: "" });

  const navigate = useNavigate();

  const fetchAll = async () => {
    try {
      const [dRes, fRes] = await Promise.all([
        axiosInstance.get("/devis?includeFactures=1"),
        axiosInstance.get("/factures?libre=1"),
      ]);

      const dSorted = (dRes.data || []).sort((a, b) => {
        const da = new Date(a.date_devis || a.created_at || 0);
        const db = new Date(b.date_devis || b.created_at || 0);
        if (db - da !== 0) return db - da;
        return (b.id || 0) - (a.id || 0);
      });

      const fSorted = (fRes.data || []).sort((a, b) => {
        const da = new Date(a.date || a.date_facture || a.created_at || 0);
        const db = new Date(b.date || b.date_facture || b.created_at || 0);
        if (db - da !== 0) return db - da;
        return (b.id || 0) - (a.id || 0);
      });

      // Exclure les factures déjà liées à un devis
      const linkedIds = new Set();
      for (const d of dSorted) {
        for (const f of Array.isArray(d.factures) ? d.factures : []) {
          if (f?.id != null) linkedIds.add(Number(f.id));
        }
      }
      const fLibresFiltrees = fSorted.filter((f) => !linkedIds.has(Number(f?.id ?? 0)));

      setDevisList(dSorted);
      setFacturesLibres(fLibresFiltrees);
    } catch (error) {
      console.error("❌ Erreur chargement documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const onStorage = (e) => {
      if (e.key === "docs:refresh") fetchAll();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const textMatch = (hay, needle) =>
    String(hay || "").toLowerCase().includes(String(needle).toLowerCase());

  // ======= Filtres communs =======
  const inRange = (dateIso) => {
    const t = dateIso ? new Date(dateIso).getTime() : NaN;
    const fromOk = !filters.from || (Number.isFinite(t) && t >= new Date(filters.from).getTime());
    const toOk = !filters.to || (Number.isFinite(t) && t <= new Date(filters.to).getTime() + 24 * 3600 * 1000 - 1);
    return fromOk && toOk;
  };
  const inAmount = (ttc) => {
    const v = Number(ttc || 0);
    const minOk = !filters.min || v >= Number(filters.min);
    const maxOk = !filters.max || v <= Number(filters.max);
    return minOk && maxOk;
  };
  const matchClient = (name) =>
    !filters.client || String(name || "").toLowerCase().includes(String(filters.client).toLowerCase());

  // ======= Listes filtrées (recherche + filtres) =======
  const devisFiltered = useMemo(() => {
    if (filters.kind === "factures") return [];
    return devisList.filter((d) => {
      const dateRef = d.date_devis || d.created_at;
      const nom = d.client_nom;
      const ttc = d.total_ttc;
      const passes =
        (!q || [d.numero_devis, nom, d.client_adresse, d.projet_nom].some((v) => textMatch(v, q))) &&
        matchClient(nom) &&
        inRange(dateRef) &&
        inAmount(ttc);
      return passes;
    });
  }, [devisList, q, filters]);

  const facturesFiltered = useMemo(() => {
    if (filters.kind === "devis") return [];
    return facturesLibres.filter((f) => {
      const numero = f.numero || f.numero_facture;
      const nom = f.client_nom || f.client?.nom;
      const adr = f.client_adresse || f.client?.adresse;
      const projet = f.projet_nom || f.projet || f.nom_projet || f.titre || f.objet;
      const dateRef = f.date || f.date_facture || f.created_at;
      const ttc = f.montant_ttc ?? f.total_ttc ?? f.total;
      const passes =
        (!q || [numero, nom, adr, projet].some((v) => textMatch(v, q))) &&
        matchClient(nom) &&
        inRange(dateRef) &&
        inAmount(ttc);
      return passes;
    });
  }, [facturesLibres, q, filters]);

  // Groupes par mois des listes filtrées
  const groupes = useMemo(() => {
    const map = new Map();
    for (const d of devisFiltered) {
      const key = toMonthKey(d.date_devis || d.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ kind: "devis", data: d, ts: new Date(d.date_devis || d.created_at || 0).getTime() });
    }
    for (const f of facturesFiltered) {
      const key = toMonthKey(f.date || f.date_facture || f.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ kind: "facture", data: f, ts: new Date(f.date || f.date_facture || f.created_at || 0).getTime() });
    }
    const ordered = Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    for (const [, arr] of ordered) arr.sort((a, b) => b.ts - a.ts);
    return ordered;
  }, [devisFiltered, facturesFiltered]);

  // Options clients pour le dropdown identique
  const clientOptions = useMemo(() => {
    const set = new Set([
      ...devisList.map((d) => d.client_nom).filter(Boolean),
      ...facturesLibres.map((f) => f.client_nom || f.client?.nom).filter(Boolean),
    ]);
    return Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );
  }, [devisList, facturesLibres]);

  if (loading) return <p className="text-center py-10">⏳ Chargement des documents…</p>;

  return (
    <div className="p-1 space-y-6">
      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-slate-700 text-left">Devis &amp; Factures</h1>
      </div>

      {/* Barre du haut : identique Clients (Filtres collés au champ) */}
      <TopBar
        q={q}
        setQ={setQ}
        filters={filters}
        setFilters={setFilters}
        clientOptions={clientOptions}
        navigate={navigate}
        onExport={() => setExportOpen(true)}
      />

      {/* Liste groupée */}
      {groupes.length === 0 && <p className="text-center text-gray-500">Aucun document pour le moment.</p>}

      {groupes.map(([monthKey, items]) => (
        <section key={monthKey} className="space-y-6">
          <h2 className="text-lg font-semibold capitalize">{toMonthLabel(monthKey)}</h2>
          <div className="space-y-8">
            {items.map((it) =>
              it.kind === "devis" ? (
                <DevisCard
                  key={`d-${it.data.id}`}
                  devis={it.data}
                  setDevisList={setDevisList}
                  onFacturer={(d) => {
                    setSelectedDevis(d);
                    setShowFactureModal(true);
                  }}
                />
              ) : (
                <FactureLibreCard
                  key={`f-${it.data.id}`}
                  facture={it.data}
                  onEtatChange={(newEtat) =>
                    // Maj locale simple (liste factures libres)
                    setFacturesLibres((prev) => prev.map((f) => (f.id === it.data.id ? { ...f, etat: newEtat } : f)))
                  }
                />
              )
            )}
          </div>
        </section>
      ))}

      {/* === Popups === */}
      {showFactureModal && selectedDevis && (
        <FactureModal
          visible={showFactureModal}
          onClose={() => setShowFactureModal(false)}
          devis={selectedDevis}
          onSelectType={(type) => {
            setShowFactureModal(false);
            const t = String(type).toLowerCase();
            if (t === "acompte") {
              if (hasFactureType(selectedDevis, "acompte")) {
                navigate(`/facture-acompte?devisId=${selectedDevis.id}`);
              } else {
                setShowAcompteModal(true);
              }
            } else if (t === "partielle") {
              setShowPartielleModal(true);
            } else if (t === "finale") {
              navigate(`/facture-finale?devisId=${selectedDevis.id}`);
            }
          }}
        />
      )}

      {showAcompteModal && selectedDevis && (
        <ModalAcompte
          visible={showAcompteModal}
          devis={selectedDevis}
          onClose={() => setShowAcompteModal(false)}
          onValider={({ mode, value }) => {
            setShowAcompteModal(false);
            const q = new URLSearchParams({
              devisId: String(selectedDevis.id),
              mode: String(mode),
              value: String(value),
            });
            navigate(`/facture-acompte?${q.toString()}`);
          }}
        />
      )}

      {showPartielleModal && selectedDevis && (
        <ModalPartielle
          visible={showPartielleModal}
          devis={selectedDevis}
          onClose={() => setShowPartielleModal(false)}
          onValider={({ mode, value }) => {
            setShowPartielleModal(false);
            const q = new URLSearchParams({
              devisId: String(selectedDevis.id),
              mode: String(mode),
              value: String(value),
            });
            navigate(`/facture-partielle?${q.toString()}`);
          }}
        />
      )}

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        devis={devisFiltered}
        factures={facturesFiltered}
        defaults={{ what: "factures", fmt: "csv" }}
      />
    </div>
  );
}

/* ================= Cartes ================= */
function DevisCard({ devis, setDevisList, onFacturer }) {
  const navigate = useNavigate();
  const factures = Array.isArray(devis.factures) ? devis.factures : [];
  const totalFactures = sumFacturesPayees(factures);
  const resteDu = Math.max(0, Number(devis.total_ttc || 0) - totalFactures);
  const numeroDevis = fmtNum("devis", devis.numero_devis, devis.id);

  const deviseBadgeCls = isDevisAccepte(devis) ? "bg-sky-100 text-sky-800" : "bg-gray-100 text-gray-800";
  const ht = Number(devis.total_ht ?? 0);
  const ttc = Number(devis.total_ttc ?? 0);
  const tva = Math.max(0, ttc - ht);
  const stripeCls = isDevisAccepte(devis) ? "bg-sky-300" : "bg-slate-300";

  return (
    <div className="relative rounded-lg border border-gray-200 shadow-sm overflow-visible bg-white">
      {/* Repère latéral */}
      <span className={`absolute left-0 top-0 h-full w-[3px] ${stripeCls}`} aria-hidden />

      <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold">
            <span className="text-slate-700">Devis</span>
            <span className="text-gray-300">•</span>
            <span className="text-base leading-none">{numeroDevis}</span>
            {devis.projet_nom && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-gray-700 truncate max-w-[46ch] sm:max-w-[60ch]">{devis.projet_nom}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-3 text-sm flex-shrink-0">
          <span className={`px-2 py-1 rounded ${deviseBadgeCls}`}>
            Devisé: <strong>{money(ttc)}</strong>
          </span>
          <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800">
            Facturé: <strong>{money(totalFactures)}</strong>
          </span>
          <span className={`px-2 py-1 rounded ${resteDu > 0 ? "bg-amber-100" : "bg-emerald-100"}`}>
            Reste: <strong>{money(resteDu)}</strong>
          </span>
        </div>
      </div>

      <div className="px-3 py-4 space-y-3">
        {/* Ligne cliquable */}
        <div
          role="button"
          tabIndex={0}
          className="flex items-center justify-between gap-4 px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-300"
          onClick={() => navigate(`/afficher-devis?id=${devis.id}`)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/afficher-devis?id=${devis.id}`)}
          title="Ouvrir l’aperçu du devis"
        >
          <div className={INFO_GRID}>
            <span className="text-slate-800">
              {devis.date_devis ? new Date(devis.date_devis).toLocaleDateString("fr-FR") : "—"}
            </span>
            <span className="text-slate-800 truncate">N° {numeroDevis}</span>
            <span className="text-slate-800 truncate">{devis.client_nom || "—"}</span>
            <span className="text-slate-800 truncate">{devis.client_adresse || "Adresse non renseignée"}</span>
          </div>

          <div className={METRICS_GRID}>
            <span className={METRIC_CELL}>HT:&nbsp;<strong className={METRIC_VALUE}>{money(ht)}</strong></span>
            <span className={METRIC_CELL}>TVA:&nbsp;<strong className={METRIC_VALUE}>{money(tva)}</strong></span>
            <span className={METRIC_CELL}>TTC:&nbsp;<strong className={METRIC_VALUE}>{money(ttc)}</strong></span>
          </div>

          {/* Statut (à droite) — ne déclenche pas la navigation */}
          <div
            className="flex items-center gap-3"
            onClick={stopRowNav}
            onMouseDown={stopRowNav}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <DevisEtatControl
              devis={devis}
              onChange={(newEtat) =>
                setDevisList((prev) =>
                  prev.map((d) => {
                    if (d.id !== devis.id) return d;
                    const key = labelToKeyDevis(newEtat);
                    const label = keyToLabelDevis(key);
                    return { ...d, status: key, etat: label };
                  })
                )
              }
            />
          </div>
        </div>

        {/* Factures liées */}
        {factures.length > 0 && (
          <div className="col-span-4">
            <div className="text-xs uppercase tracking-wide text-gray-900 mb-2">Factures</div>
            <div className="divide-y border rounded bg-white">
              {factures.map((f) => (
                <FactureLieeRow key={f.id} devis={devis} f={f} setDevisList={setDevisList} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ====== Facture liée : statut au bout de la ligne + stopPropagation ====== */
function FactureLieeRow({ devis, f, setDevisList }) {
  const navigate = useNavigate();
  if (!f) return null;

  const numero = fmtNum("facture", f?.numero || f?.numero_facture, f?.id);
  const typeLib = String(f?.type_facture ?? f?.type ?? "facture").toLowerCase();
  const dateRaw = f?.date || f?.date_facture || f?.created_at;
  const dateStr = dateRaw ? new Date(dateRaw).toLocaleDateString("fr-FR") : "—";
  const ht = Number(f?.montant_ht ?? f?.total_ht ?? 0);
  const ttc = Number(f?.montant_ttc ?? f?.total_ttc ?? 0);
  const tva = Math.max(0, ttc - ht);

  return (
    <div
      role="button"
      tabIndex={0}
      className="flex items-center justify-between gap-4 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-300"
      onClick={() => {
        const type = String(f?.type_facture ?? f?.type ?? "").toLowerCase();
        if (type === "acompte") navigate(`/facture-acompte?devisId=${devis.id}&factureId=${f.id}`);
        else if (type === "partielle") navigate(`/facture-partielle?devisId=${devis.id}&factureId=${f.id}`);
        else if (type === "finale") navigate(`/facture-finale?devisId=${devis.id}`);
        else navigate(`/afficher-facture?id=${f.id}`);
      }}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.currentTarget.click()}
      title="Ouvrir l’aperçu de la facture"
    >
      <div className={INFO_GRID}>
        <span className="text-slate-800">{dateStr}</span>
        <span className="text-slate-800 truncate">{numero}</span>
        <span className="text-slate-800 capitalize">{typeLib}</span>
        <span className="text-slate-400 truncate">—</span>
      </div>

      <div className={METRICS_GRID}>
        <span className={METRIC_CELL}>
          HT:&nbsp;<strong className={METRIC_VALUE}>{money(ht)}</strong>
        </span>
        <span className={METRIC_CELL}>
          TVA:&nbsp;<strong className={METRIC_VALUE}>{money(tva)}</strong>
        </span>
        <span className={METRIC_CELL}>
          TTC:&nbsp;<strong className={METRIC_VALUE}>{money(ttc)}</strong>
        </span>
      </div>

      <div
        className="flex items-center gap-4"
        onClick={stopRowNav}
        onMouseDown={stopRowNav}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <FactureEtatControl
          facture={f}
          enableRelance={false}
          onChange={async (newEtat) => {
            const prevEtat = f.etat;
            setDevisList((prev) =>
              prev.map((d) =>
                d.id === devis.id
                  ? { ...d, factures: (d.factures || []).map((x) => (x.id === f.id ? { ...x, etat: newEtat } : x)) }
                  : d
              )
            );
            try {
              await persistEtatFacture(axiosInstance, f.id, newEtat);
              localStorage.setItem("docs:refresh", String(Date.now()));
            } catch (err) {
              console.error("Erreur mise à jour état:", err);
              setDevisList((prev) =>
                prev.map((d) =>
                  d.id === devis.id
                    ? { ...d, factures: (d.factures || []).map((x) => (x.id === f.id ? { ...x, etat: prevEtat } : x)) }
                    : d
                )
              );
              alert("❌ Impossible de mettre à jour l'état de la facture.");
            }
          }}
        />
      </div>
    </div>
  );
}


function FactureLibreCard({ facture, onEtatChange }) {
  const navigate = useNavigate();
  const numero = fmtNum("facture", facture.numero || facture.numero_facture, facture.id);
  const projet =
    facture.projet_nom || facture.projet || facture.nom_projet || facture.titre || facture.objet || "—";
  const clientNom =
    facture.client_nom ||
    facture.client?.nom ||
    facture?.meta?.client_nom ||
    facture?.meta?.client?.nom ||
    "—";
  const clientAdr =
    facture.client_adresse ||
    facture.client?.adresse ||
    facture?.meta?.client_adresse ||
    facture?.meta?.client?.adresse ||
    "Adresse non renseignée";

  const dateStr = facture.date ? new Date(facture.date).toLocaleDateString("fr-FR") : "—";
  const ht = Number(facture.montant_ht ?? facture.total_ht ?? 0);
  const ttc = Number(facture.montant_ttc ?? facture.total_ttc ?? 0);
  const tva = Math.max(0, ttc - ht);
  const reste = isFacturePayee(facture) ? 0 : ttc;

  const stripeCls = isFacturePayee(facture) ? "bg-emerald-300" : "bg-amber-300";

  return (
    <div className="relative rounded-lg border border-gray-200 shadow-sm overflow-visible bg-white">
      {/* repère latéral */}
      <span className={`absolute left-0 top-0 h-full w-[3px] ${stripeCls}`} aria-hidden />

      {/* bandeau haut */}
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold">
            <span className="text-slate-700">Facture</span>
            <span className="text-gray-300">•</span>
            <span className="text-base leading-none">{numero}</span>
            {projet && projet !== "—" && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-gray-700 truncate max-w-[46ch] sm:max-w-[60ch]" title={projet}>
                  {projet}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-5 text-sm flex-shrink-0">
          <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800">
            Facturé: <strong>{money(ttc)}</strong>
          </span>
          <span className={`px-2 py-1 rounded ${reste > 0 ? "bg-amber-100" : "bg-emerald-100"}`}>
            Reste: <strong>{money(reste)}</strong>
          </span>
        </div>
      </div>

      {/* ligne cliquable avec statut à droite */}
      <div className="px-3 py-4">
        <div
          role="button"
          tabIndex={0}
          className="flex items-center justify-between gap-4 px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-300"
          onClick={() => navigate(`/afficher-facture?id=${facture.id}`)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/afficher-facture?id=${facture.id}`)}
          title="Ouvrir l’aperçu de la facture"
        >
          <div className={INFO_GRID}>
            <span className="text-slate-800">{dateStr}</span>
            <span className="text-slate-800 truncate">N° {numero}</span>
            <span className="text-slate-800 truncate">{clientNom}</span>
            <span className="text-slate-800 truncate">{clientAdr}</span>
          </div>

          <div className={METRICS_GRID}>
            <span className={METRIC_CELL}>HT:&nbsp;<strong className={METRIC_VALUE}>{money(ht)}</strong></span>
            <span className={METRIC_CELL}>TVA:&nbsp;<strong className={METRIC_VALUE}>{money(tva)}</strong></span>
            <span className={METRIC_CELL}>TTC:&nbsp;<strong className={METRIC_VALUE}>{money(ttc)}</strong></span>
          </div>

          {/* statut au bout de la ligne (n’ouvre pas la page) */}
          <div
            className="flex items-center gap-3"
            onClick={stopRowNav}
            onMouseDown={stopRowNav}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <FactureEtatControl
              facture={facture}
              enableRelance={true}
              onChange={(newEtat) => onEtatChange?.(newEtat)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


function hasFactureType(devis, type) {
  const t = String(type || "").toLowerCase();
  return (Array.isArray(devis?.factures) ? devis.factures : []).some(
    (f) => String(f.type_facture ?? f.type ?? "").toLowerCase() === t
  );
}
